import { useEffect, useState } from 'react';
import { getUserAvatarUrl } from '../../../../constants/static-path-constants';
import { useDispatch } from 'react-redux';
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Autocomplete, Switch, ListItem, ListItemAvatar, Avatar, ListItemText, Box, Divider, TextField, Button, CircularProgress } from '@mui/material';
import Grid2 from '@mui/material/Grid';
import { useFormik } from 'formik';
import axios from 'axios';
import { emailer } from '../../../../constants/api-constants';
import { SupportCategory, SupportSubCategory } from './ContactSupport';
import FileUpload from '../../../forms-components/file-upload/FileUpload';
import { enqueueNotification } from '../../../../../store/notification/action';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import ValidationError from '../../../ValidationError';
import { searchUsers, UserSearchResult } from '../../../../../api/users';

interface BugFormProps {
  handleClose: () => void
}

const ContactSupportForm = ({ handleClose }: BugFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const [onBehalfOf, setOnBehalfOf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (!onBehalfOf) {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [onBehalfOf]);

  useEffect(() => {
    if (!onBehalfOf || searchTerm.trim().length === 0) {
      setSearchResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const delay = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await searchUsers(searchTerm.trim(), controller.signal);
        if (!controller.signal.aborted) {
          setSearchResults(data);
        }
      } catch {
        if (!controller.signal.aborted) {
          setSearchResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    }, 500);
    return () => {
      clearTimeout(delay);
      controller.abort();
    };

  }, [onBehalfOf, searchTerm]);

  const formik = useFormik({
    initialValues: {
      selectedUser: null,
      category: SupportCategory.DEFAULT,
      subcategory: SupportSubCategory.DEFAULT,
      identifier: location.pathname.startsWith('/campaign') || location.pathname.startsWith('/campaign-plan') ? (params.id ?? params.campaign_id ?? '') : '',
      summary: '',
      message: '',
      files: []
    },
    onSubmit: async (values: any) => {
      setSubmitting(true);

      const data = new FormData();
      data.append('onBehalfOf', JSON.stringify(values.selectedUser));
      data.append('category', values.category);
      data.append('subcategory', values.subcategory);
      data.append('identifier', values.identifier);
      data.append('summary', values.summary);
      data.append('message', values.message);

      values.files.forEach((file: File) => {
        // @ts-ignore
        data.append('files', file);
      });

      try {
        const response = await axios.post(`${emailer}/contact-support`, data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data?.attachmentUploadFailed) {
          dispatch(enqueueNotification('Your support request was created successfully, but we could not upload the attachments. Please add them manually to your JIRA ticket or reach out the support team.', 'warning', 8000));
        } else {
          dispatch(enqueueNotification('Thank you for your message. The project team has been informed and will come back to you shortly.', 'info', 5000));
        }
        setSubmitting(false);
        handleClose();
      } catch (error) {
        dispatch(enqueueNotification('Something went wrong when submitting your message, please try again.', 'error', 5000));
        setSubmitting(false);
        handleClose();
      }
    },
    validate: (values: any) => {
      const errors: {
        selectedUser?: string
        category?: string
        subcategory?: string
        identifier?: string
        summary?: string
        message?: string
      } = {};

      if (onBehalfOf && !values.selectedUser) {
        errors.selectedUser = 'Please fill out this field.';
      }

      if (!values.category) {
        errors.category = 'Please fill out this field.';
      }

      if (!values.subcategory) {
        errors.subcategory = 'Please fill out this field.';
      }

      if (![SupportSubCategory.HOME, SupportSubCategory.OTHER].includes(values.subcategory) && !values.identifier) {
        errors.identifier = 'Please fill out this field.';
      }

      if (!values.summary) {
        errors.summary = 'Please fill out this field.';
      }

      if (!values.message) {
        errors.message = 'Please fill out this field.';
      }

      return errors;
    }
  });

  const handleFileDelete = (fileToDelete: File) => {
    formik.setFieldValue('files', formik.values.files.filter((file: File) => file.name !== fileToDelete.name));
  }

  return (
    <Box>
      <Divider sx={{ mt: 4, mb: 2 }} />
      <form id='bug-form' onSubmit={formik.handleSubmit}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <FormControlLabel
              label={t('top_bar.contact_support.on_behalf')}
              control={
                <Switch
                  color='success'
                  checked={onBehalfOf}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setOnBehalfOf(checked);
                    if (!checked) {
                      formik.setFieldValue('selectedUser', null);
                    }
                  }}
                />
              }
            />
          </Grid2>
          <Grid2 size={8}>
            <FormControl fullWidth size='small'>
              {
                onBehalfOf &&
                <Autocomplete
                  options={searchResults}
                  value={formik.values.selectedUser}
                  onChange={(e, value) => formik.setFieldValue('selectedUser', value)}
                  inputValue={searchTerm}
                  onInputChange={(_, value) => setSearchTerm(value)}
                  size='small'
                  isOptionEqualToValue={(option, value) => option.wwid === value.wwid}
                  getOptionLabel={(option) => option.name}
                  loading={searchLoading}
                  filterOptions={(options) => options}
                  renderOption={(props, option) => (
                    <ListItem {...props}>
                      <ListItemAvatar>
                        <Avatar src={getUserAvatarUrl(option.wwid, option.name)} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={option.name}
                        secondary={`${option.wwid} · ${option.email}`}
                      />
                    </ListItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('top_bar.contact_support.on_behalf')}
                      variant='outlined'
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {searchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              }
              {
                formik.errors.selectedUser && formik.touched.selectedUser &&
                <ValidationError message={formik.errors.selectedUser as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth size='small'>
              <InputLabel id='support-option-label'>Category</InputLabel>
              <Select
                labelId='support-option-label'
                label='Category'
                value={formik.values.category}
                onChange={(event) => formik.setFieldValue('category', event.target.value as SupportCategory)}
                fullWidth
                size='small'
              >
                <MenuItem value={SupportCategory.BUG}>{t('top_bar.contact_support.bug')}</MenuItem>
                <MenuItem value={SupportCategory.FEATURE}>{t('top_bar.contact_support.feature')}</MenuItem>
                <MenuItem value={SupportCategory.HELP}>{t('top_bar.contact_support.help')}</MenuItem>
              </Select>
              {
                formik.errors.category && formik.touched.category &&
                <ValidationError message={formik.errors.category as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth size='small'>
              <InputLabel id='support-subcategory-label'>Sub Category</InputLabel>
              <Select
                labelId='support-subcategory-label'
                label='Sub Category'
                value={formik.values.subcategory}
                onChange={(event) => formik.setFieldValue('subcategory', event.target.value)}
                fullWidth
                size='small'
              >
                <MenuItem value={SupportSubCategory.HOME}>{t('top_bar.contact_support.home')}</MenuItem>
                <MenuItem value={SupportSubCategory.MONITORING}>{t('top_bar.contact_support.monitoring')}</MenuItem>
                <MenuItem value={SupportSubCategory.KPI}>{t('top_bar.contact_support.kpi')}</MenuItem>
                <MenuItem value={SupportSubCategory.TARGET}>{t('top_bar.contact_support.target')}</MenuItem>
                <MenuItem value={SupportSubCategory.FORMS}>{t('top_bar.contact_support.forms')}</MenuItem>
                <MenuItem value={SupportSubCategory.BUILDER}>{t('top_bar.contact_support.builder')}</MenuItem>
                <MenuItem value={SupportSubCategory.CLONING}>{t('top_bar.contact_support.cloning')}</MenuItem>
                <MenuItem value={SupportSubCategory.OTHER}>{t('top_bar.contact_support.other')}</MenuItem>
              </Select>
              {
                formik.errors.subcategory && formik.touched.subcategory &&
                <ValidationError message={formik.errors.subcategory as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth size='small'>
              <TextField
                label={t('top_bar.contact_support.campaign_id')}
                value={formik.values.identifier}
                onChange={(e) => formik.setFieldValue('identifier', e.target.value)}
                size='small'
              />
              {
                formik.errors.identifier && formik.touched.identifier &&
                <ValidationError message={formik.errors.identifier as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth size='small'>
              <TextField
                label='Summary'
                value={formik.values.summary}
                onChange={(e) => formik.setFieldValue('summary', e.target.value)}
                size='small'
              />
              {
                formik.errors.summary && formik.touched.summary &&
                <ValidationError message={formik.errors.summary as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth size='small'>
              <TextField
                label='Message'
                value={formik.values.message}
                onChange={(e) => formik.setFieldValue('message', e.target.value)}
                multiline
                rows={4}
                size='small'
              />
              {
                formik.errors.message && formik.touched.message &&
                <ValidationError message={formik.errors.message as string} />
              }
            </FormControl>
          </Grid2>

          <Grid2 size={12}>
            <FileUpload
              upload={(files: any) => formik.setFieldValue('files', Array.from(files))}
              styles={{
                dropContainer: {
                  height: '200px',
                  width: '100%'
                }
              }}
            />

            {
              formik.values.files.map((file: File) => {
                return (
                  <Box key={file.name} className="file-details-div">
                    <p className="file-title">{file.name}</p>
                    <DeleteIcon onClick={() => handleFileDelete(file)} className="delete-button" />
                  </Box>
                )
              })
            }
          </Grid2>

          <Grid2 className='contact-support-actions' size={12}>
            <Button type='submit' form='bug-form' variant='contained' sx={{ mr: 1 }} disabled={submitting}>Send</Button>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          </Grid2>
        </Grid2>

      </form>
    </Box>
  )
}

export default ContactSupportForm;
